import React, { useEffect, useRef, useState } from 'react';

import { useWindowSize } from '../../../hooks/useWindowSize';

import debounce from '../../../helpers/debounce';
import { applyOpacityToHex } from '../../../helpers/applyOpacityToHex';

const TruncatingLabelList = ({ labels = [], showRemainingCount = false }) => {
    const containerRef = useRef(null);
    const labelRefs = useRef([]);

    const labelsCount = labels?.length || 0;
    const [visibleCount, setVisibleCount] = useState(labelsCount);
    const [isMounted, setIsMounted] = useState(false);

    const debouncedResizeHandler = useRef(debounce(countVisibleLabels, 100))?.current;

    const { width: screenWidth } = useWindowSize();

    function countVisibleLabels() {
        if (!labelsCount) return;

        const container = containerRef.current;
        if (!container) return;

        let containerWidth = container.clientWidth;

        const labelsWidth = labels.map((_, i) => {
            const el = labelRefs.current[i];
            if (!el) return 0;

            const normalizedWidth = Math.ceil(el.offsetWidth);
            return normalizedWidth;
        });

        if (!isMounted) setIsMounted(true);

        const allLabelsWidth =
            labelsWidth.reduce((sum, width) => sum + width, 0) + (labelsCount - 1) * 8;
        const allLabelsVisible = allLabelsWidth <= containerWidth;

        if (allLabelsVisible) {
            setVisibleCount(labelsCount);
            return;
        }

        if (showRemainingCount) {
            containerWidth = Math.max(containerWidth - 84, 0); // 84 px for "+ n more"
        }

        let total = 0;
        let fitCount = 0;

        for (let i = 0; i < labelsCount; i++) {
            const elWidth = (i === 0 ? labelsWidth[i] : labelsWidth[i] + 8) || 0;

            if (total + elWidth <= containerWidth) {
                total += elWidth;
                fitCount++;
            } else {
                break;
            }
        }

        setVisibleCount(Math.max(1, fitCount));
    }

    useEffect(() => {
        countVisibleLabels();
    }, [labels]);

    useEffect(() => {
        debouncedResizeHandler();
    }, [screenWidth]);

    const visibleLabels = labels?.slice(0, visibleCount) || [];
    const remainingCount = labelsCount - visibleCount;

    return (
        <>
            <div
                className="w-full max-w-full flex items-center gap-2 min-h-[24px] overflow-hidden"
                ref={containerRef}
            >
                {visibleLabels.map(({ name, icon_color }, index) => (
                    <div
                        key={index}
                        className="h-[24px] flex items-center px-2 font-body-medium text-[12px] rounded-2 whitespace-nowrap truncate"
                        style={{
                            color: icon_color,
                            backgroundColor: applyOpacityToHex(icon_color, 20),
                            opacity: isMounted ? 1 : 0,
                        }}
                    >
                        {name}
                    </div>
                ))}
                {showRemainingCount && remainingCount > 0 && (
                    <div className="font-body text-body-regular-s text-neutral-300 whitespace-nowrap">
                        +{remainingCount} more
                    </div>
                )}
            </div>

            <div
                className="absolute -top-[9999px] left-0 flex gap-2 items-start opacity-0 pointer-events-none"
                style={{ visibility: 'hidden' }}
            >
                {labels.map(({ name, icon_color }, index) => (
                    <div
                        className="min-w-fit"
                        key={`hidden-${index}`}
                        ref={(el) => (labelRefs.current[index] = el)}
                    >
                        <div className="px-2 py-1 font-body-medium text-[12px] rounded-2">
                            {name}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default TruncatingLabelList;
